import React, {
  useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useIntl } from 'react-intl';
import PropTypes, { element as Element } from 'prop-types';
import { IconClose } from '@americanexpress/dls-icons';
import { IconButton, Input } from '@americanexpress/dls-react';
import clsx from 'clsx';
import { useExperimentContext } from '@americanexpress/one-experiments';
import locationContext from '../../context/locationsContext';
import { isFlightOrPackageLOB } from '../../utils/suggestionsUtils';
import styles from './Input.scss';
import { HOMEPAGE_REDESIGN_EXPERIMENT } from '../../constants/experimentConstants';

const InputField = ({
  hasPopularLocations,
  hasRecentSearch,
  isFullScreen,
  inputID,
  clearID,
  fullscreenInputRefocusRef,
}) => {
  const {
    state: {
      hasWarning,
      shouldDisplayResults,
      location: {
        query,
        label,
        airportCode,
        name,
      },
      focusedLocationRef,
      inputWrapperRef,
    },
    actions: {
      setQuery,
      onFocus,
      onClick,
      onClear,
      onBlur,
      onKeyboardNavigate,
    },
    config: {
      lineOfBusiness,
      minimumQueryLength,
      ariaLabel,
      labelText,
    },
  } = useContext(locationContext);
  const [isClearButtonVisible, setIsClearButtonVisible] = useState(false);
  const { formatMessage } = useIntl();
  const inputRef = useRef(null);

  const { selectors: { getExperimentVariant } } = useExperimentContext();
  const experimentData = getExperimentVariant(HOMEPAGE_REDESIGN_EXPERIMENT);

  const isVariantRedesign = useMemo(() => experimentData
    && experimentData.dimension === 'search_widget'
    && experimentData.variant !== 'control', [experimentData?.dimension, experimentData?.variant]);

  let value = (label || name || query) ?? '';
  if (isFlightOrPackageLOB(lineOfBusiness) && airportCode) {
    value = `${airportCode}, ${label}`;
  }

  const isListboxExpanded = shouldDisplayResults && (query?.length >= minimumQueryLength);
  const activeDescendantId = focusedLocationRef >= 0 ? `suggestion-${focusedLocationRef}` : '';
  const isPopularLocationsOpen = hasPopularLocations && value === '' && shouldDisplayResults;
  const ariaControlsId = isPopularLocationsOpen ? 'popularLocationsListbox' : 'suggestionsListbox';

  const onSearchQueryChange = (event) => {
    setQuery(event.target.value);
  };

  const onClearHandler = () => {
    onClear();
    setIsClearButtonVisible(false);
    inputRef.current?.focus();
  };

  const onFocusHandler = (event) => {
    setIsClearButtonVisible(true);
    onFocus(event);
  };

  const onBlurHandler = (event) => {
    setIsClearButtonVisible(false);
    if (typeof onBlur === 'function') {
      onBlur(event);
    }
  };

  const combineRefs = (...refs) => (element) => {
    refs.forEach((ref) => {
      if (ref != null) {
        // eslint-disable-next-line no-param-reassign -- Ref Update
        ref.current = element;
      }
    });
  };

  useEffect(() => {
    if (isFullScreen) {
      // Due to default Modal behavior moving focus to the first element,
      // the delay is needed here to move the focus to the input after Modal mounts.
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, []);

  const combineInputAndRefocus = (element) => {
    combineRefs(inputRef, fullscreenInputRefocusRef)(element);
  };

  return (
    <div className={clsx('position-relative', styles.inputWrapper)} ref={inputWrapperRef}>
      <Input
        id={inputID}
        ref={combineInputAndRefocus}
        label={isVariantRedesign ? labelText : undefined}
        value={value}
        role="combobox"
        childrenPosition="end"
        autoComplete="on"
        status={hasWarning ? 'error' : 'default'}
        onChange={onSearchQueryChange}
        onKeyDown={onKeyboardNavigate}
        onClick={onClick}
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
        autoFocus={isFullScreen}
        aria-label={ariaLabel || labelText}
        aria-activedescendant={activeDescendantId}
        aria-expanded={isListboxExpanded || isPopularLocationsOpen || hasRecentSearch}
        aria-controls={isListboxExpanded && ariaControlsId}
        aria-describedby={`${inputID}-status-message`}
        className={isVariantRedesign ? '' : styles.inputField}
      />
      <IconButton
        id={clearID}
        variant="secondary"
        screenReaderLabel={formatMessage({ id: 'clearButton.ariaLabel' })}
        onClick={onClearHandler}
        tabIndex={0}
        onFocus={() => setIsClearButtonVisible(true)}
        onBlur={onBlurHandler}
        style={{ top: isVariantRedesign ? '35px' : '3px' }}
        className={clsx(
          'position-absolute bg-none pad-0 border-0',
          styles.clearButton,
          isClearButtonVisible ? '' : 'sr-only')}
      >
        <IconClose />
      </IconButton>
    </div>
  );
};

InputField.propTypes = {
  hasPopularLocations: PropTypes.bool,
  hasRecentSearch: PropTypes.bool,
  isFullScreen: PropTypes.bool,
  inputID: PropTypes.string.isRequired,
  clearID: PropTypes.string.isRequired,
  fullscreenInputRefocusRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

InputField.defaultProps = {
  hasPopularLocations: false,
  hasRecentSearch: false,
  isFullScreen: false,
};

export { InputField as TestableInput };
export default InputField;
